/**
 * The campaign page: what a donor sees when they follow a campaign link.
 *
 * Every number on this page comes from the API, and the API computes it from
 * the ledger. There is no number in this file, and no fallback: a campaign
 * with no money yet says so in words, because a "0 raised" that reads as "this
 * campaign has never been funded" is not the same fact as "nothing has come
 * in yet".
 *
 * The page is bilingual like the rest of the site: the title and the
 * description come in both languages and the page shows the one the donor's
 * browser asked for.
 */

import type { PublicCampaign } from '@qalam/shared';
import { apiFetch } from '@qalam/shared/frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

/** A sponsorship track, with its report. The numbers are null when the
 * group is too small to report, and the page says so. */
export interface CampaignTrack {
  key: string;
  titleAr: string;
  titleEn: string;
  goalSeats: number;
  seatsFunded: number | null;
  donors: number | null;
  suppressed: boolean;
}

export interface CampaignPageProps {
  /** Injected so a test can serve its own campaign without a network. */
  load?: (slug: string) => Promise<PublicCampaign | null>;
  /** The sponsorship tracks, with their reports. */
  loadTracks?: (slug: string) => Promise<CampaignTrack[]>;
}

function defaultLoad(slug: string): Promise<PublicCampaign | null> {
  return apiFetch<{ campaign: PublicCampaign }>(`/api/public/campaigns/${slug}`)
    .then((payload) => payload.campaign)
    .catch(() => null);
}

/** The tracks of a campaign, read separately so a missing list is not fatal. */
function defaultLoadTracks(slug: string): Promise<CampaignTrack[]> {
  return apiFetch<{ tracks: CampaignTrack[] }>(`/api/public/campaigns/${slug}`)
    .then((payload) => payload.tracks ?? [])
    .catch(() => []);
}

export default function CampaignPage({
  load = defaultLoad,
  loadTracks = defaultLoadTracks,
}: CampaignPageProps) {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [tracks, setTracks] = useState<CampaignTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMissing(false);
    void load(slug)
      .then((found) => {
        if (cancelled) return;
        setCampaign(found);
        if (!found) setMissing(true);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    void loadTracks(slug)
      .then((found) => {
        if (!cancelled) setTracks(found);
      })
      .catch(() => {
        if (!cancelled) setTracks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, load, loadTracks]);

  if (loading) {
    return (
      <main data-testid="campaign-page">
        <p data-testid="campaign-loading">{t('campaign.loading')}</p>
      </main>
    );
  }

  if (missing || !campaign) {
    return (
      <main data-testid="campaign-page">
        <p data-testid="campaign-missing">{t('campaign.missing')}</p>
        <Link to="/">{t('nav.home')}</Link>
      </main>
    );
  }

  const arabic = i18n.language?.startsWith('ar');
  const title = arabic ? campaign.titleAr : campaign.titleEn;
  const description = arabic ? campaign.descriptionAr : campaign.descriptionEn;
  const raised = campaign.raisedCents;

  return (
    <main data-testid="campaign-page">
      <h1 data-testid="campaign-title">{title}</h1>
      {description ? <p data-testid="campaign-description">{description}</p> : null}

      <section data-testid="campaign-progress">
        {raised === null ? (
          <p data-testid="campaign-empty">{t('campaign.noMoneyYet')}</p>
        ) : (
          <p data-testid="campaign-raised">
            {t('campaign.raised', { amount: (raised / 100).toFixed(2) })}
          </p>
        )}
        <p data-testid="campaign-seats">{t('campaign.seats', { count: campaign.seatsFunded })}</p>
        <p data-testid="campaign-contributors">
          {t('campaign.contributors', { count: campaign.contributors })}
        </p>
        <p data-testid="campaign-goal">
          {campaign.goalUnit === 'SEATS'
            ? t('campaign.goalSeats', { count: campaign.goalAmount })
            : t('campaign.goalAmount', { amount: (campaign.goalAmount / 100).toFixed(2) })}
        </p>
        {campaign.deadline ? (
          <p data-testid="campaign-deadline">
            {t('campaign.deadline', { date: campaign.deadline.slice(0, 10) })}
          </p>
        ) : null}
      </section>

      {tracks.length > 0 ? (
        <section data-testid="campaign-tracks">
          {tracks.map((track) => (
            <div key={track.key} data-testid={`track-${track.key}`}>
              <h3>{arabic ? track.titleAr : track.titleEn}</h3>
              {track.suppressed ? (
                <p data-testid={`track-suppressed-${track.key}`}>{t('campaign.trackSuppressed')}</p>
              ) : (
                <p data-testid={`track-progress-${track.key}`}>
                  {t('campaign.trackSeats', { count: track.seatsFunded ?? 0 })}
                </p>
              )}
            </div>
          ))}
        </section>
      ) : null}

      <Link to={`/give?campaign=${campaign.slug}`} data-testid="campaign-give">
        {t('campaign.give')}
      </Link>
    </main>
  );
}
